import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowLeft } from "lucide-react";
import * as api from "@/lib/api";
import { toast } from "sonner";
import { useOnline } from "@/lib/useOnline";
import { OfflineNotice } from "@/components/OfflineNotice";
import { getPendingCount } from "@/lib/sync";
import { LogoutGuardModal } from "@/components/LogoutGuardModal";

export default function ProfileSettings() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const online = useOnline();
  const [pwd, setPwd] = useState({ old: "", new: "" });
  const [saving, setSaving] = useState(false);
  const [logoutModalOpen, setLogoutModalOpen] = useState(false);

  const handleLogoutClick = async () => {
    const pending = await getPendingCount();
    if (pending === 0) {
      await logout();
      navigate("/login", { replace: true });
    } else {
      setLogoutModalOpen(true);
    }
  };

  const changePassword = async () => {
    if (pwd.new.length < 6) return toast.error("Mínimo 6 caracteres");
    setSaving(true);
    try {
      await api.changePassword({ old_password: pwd.old, new_password: pwd.new });
      toast.success("Contraseña actualizada");
      setPwd({ old: "", new: "" });
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
    finally { setSaving(false); }
  };

  return (
    <div className="max-w-xl mx-auto space-y-4">
      <div className="flex items-center gap-2">
        <Button variant="ghost" onClick={()=>navigate("/ajustes")} className="h-9 px-2"><ArrowLeft size={18} /></Button>
        <h1 className="font-heading text-2xl font-semibold">Mi perfil</h1>
      </div>
      <Card>
        <CardHeader><CardTitle className="text-base">Datos</CardTitle></CardHeader>
        <CardContent className="space-y-2 text-sm">
          <Field label="Nombre" value={user?.name} />
          <Field label="Email" value={user?.email} />
          <Field label="Rol" value={user?.role === "owner" ? "Dueño" : "Encargado"} />
        </CardContent>
      </Card>
      <Card>
        <CardHeader><CardTitle className="text-base">Cambiar contraseña</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          {!online && <OfflineNotice label="Cambiar la contraseña requiere conexión." />}
          <div><Label>Contraseña actual</Label><Input data-testid="profile-old-pwd" type="password" value={pwd.old} onChange={(e)=>setPwd({ ...pwd, old: e.target.value })} className="h-11" /></div>
          <div><Label>Nueva contraseña</Label><Input data-testid="profile-new-pwd" type="password" value={pwd.new} onChange={(e)=>setPwd({ ...pwd, new: e.target.value })} className="h-11" /></div>
          <Button onClick={changePassword} disabled={saving || !online} className="h-11 bg-emerald-700 hover:bg-emerald-800" data-testid="profile-change-pwd">Actualizar</Button>
        </CardContent>
      </Card>
      <div className="flex justify-end">
        <Button variant="outline" onClick={handleLogoutClick} className="text-red-700 border-red-200 hover:bg-red-50" data-testid="profile-logout">Cerrar sesión</Button>
      </div>
      <LogoutGuardModal
        open={logoutModalOpen}
        onOpenChange={setLogoutModalOpen}
        onLoggedOut={() => navigate("/login", { replace: true })}
      />
    </div>
  );
}

function Field({ label, value }) {
  return (
    <div className="flex justify-between border-b border-border last:border-0 py-2">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-medium">{value}</span>
    </div>
  );
}
